import React, { useState, useEffect, useRef } from 'react';
import { X, BookText, Loader2, Bookmark, Check, Volume2, Square } from 'lucide-react';
import { apiClient } from '../services/api';
import { highlightWords } from '../utils/highlightWords';
import { trackAIGenerated, trackStorySaved } from '../utils/analytics';
import { speak, stopSpeech } from '../utils/speechUtils';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface GenerateStoryModalProps {
  /** Category mode: generate from all cards in a category */
  categoryId?: number;
  categoryName?: string;
  /** Folder mode: generate from all cards in a folder */
  folderId?: number;
  /** Cards mode: generate from a specific list of card IDs */
  cardIds?: number[];
  onClose: () => void;
  onStorySaved?: () => void;
}

interface StoryResult {
  story: string;
  words_used: string[];
  totalWords: number;
}

export const GenerateStoryModal: React.FC<GenerateStoryModalProps> = ({ categoryId, categoryName, folderId, cardIds, onClose, onStorySaved }) => {
  useBodyScrollLock();
  const isCardsMode = !!cardIds && cardIds.length > 0;
  const isFolderMode = !!folderId && !isCardsMode;
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<StoryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const isSpeakingRef = useRef(false);

  const handleSpeak = (text: string) => {
    if (isSpeakingRef.current) {
      stopSpeech();
      isSpeakingRef.current = false;
      setIsSpeaking(false);
      return;
    }
    isSpeakingRef.current = true;
    setIsSpeaking(true);
    speak(text, {
      onEnd: () => { isSpeakingRef.current = false; setIsSpeaking(false); },
      onError: () => { isSpeakingRef.current = false; setIsSpeaking(false); },
    });
  };

  // Stop speech when result changes or modal unmounts
  useEffect(() => {
    return () => { stopSpeech(); };
  }, []);

  useEffect(() => {
    stopSpeech();
    isSpeakingRef.current = false;
    setIsSpeaking(false);
  }, [result]);

  const handleGenerate = async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (isCardsMode) {
        const data = await apiClient.generateTextFromCards({ card_ids: cardIds!, prompt: prompt.trim() || undefined });
        setResult({ story: data.text, words_used: data.words_used, totalWords: data.total_words_requested });
      } else if (isFolderMode) {
        const data = await apiClient.generateFolderStory(folderId!, { prompt: prompt.trim() || undefined });
        setResult({ story: data.story, words_used: data.words_used, totalWords: data.total_words_in_folder });
      } else {
        const data = await apiClient.generateStory(categoryId!, { prompt: prompt.trim() || undefined });
        setResult({ story: data.story, words_used: data.words_used, totalWords: data.total_words_in_category });
      }
      setSaved(false);
      setSaveError(null);
      trackAIGenerated('story');
    } catch (err: any) {
      const detail = err?.response?.data?.detail ?? '';
      if (err?.response?.status === 422 && detail.toLowerCase().includes('no vocabulary cards')) {
        setError('This category has no words yet. Add some vocabulary cards before generating a story.');
      } else {
        setError('Could not generate the text. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateAgain = () => {
    setResult(null);
    setSaved(false);
    setSaveError(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!result || saved || (!categoryId && !folderId)) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const payload = { story: result.story, words_used: result.words_used, prompt: prompt.trim() || undefined };
      if (isFolderMode) {
        await apiClient.saveFolderStory(folderId!, payload);
      } else {
        await apiClient.saveStory(categoryId!, payload);
      }
      setSaved(true);
      trackStorySaved();
      onStorySaved?.();
    } catch {
      setSaveError('Could not save the text. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 sm:p-6">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Generate Text</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {isCardsMode
                ? <span className="font-medium text-gray-700">{cardIds!.length} selected card{cardIds!.length !== 1 ? 's' : ''}</span>
                : <span className="font-medium text-gray-700">&quot;{categoryName}&quot;</span>
              }
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-4">
          {!result && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Theme or context <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder='e.g. "A dialogue set in a corporate office"'
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/80 focus:border-purple-300 resize-none bg-white text-sm"
                  rows={2}
                  disabled={isLoading}
                />
              </div>

              <button
                type="button"
                onClick={handleGenerate}
                disabled={isLoading}
                className="w-full px-4 py-3 text-[15px] font-medium bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-md hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-sm"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <BookText className="h-5 w-5" />
                    Generate
                  </>
                )}
              </button>

              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">{error}</div>
              )}
            </>
          )}

          {result && (
            <>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap flex-1">{highlightWords(result.story, result.words_used)}</p>
                <button
                  type="button"
                  onClick={() => handleSpeak(result.story)}
                  className={`flex-shrink-0 p-1.5 rounded-full transition-colors ${isSpeaking ? 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}
                  title={isSpeaking ? 'Stop' : 'Read aloud'}
                >
                  {isSpeaking ? <Square className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </button>
              </div>

              {result.words_used.length < result.totalWords && (
                <p className="text-xs text-gray-400 italic">
                  Text generated using {result.words_used.length} of {result.totalWords} word{result.totalWords !== 1 ? 's' : ''}.
                </p>
              )}

              {saved && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <Check className="h-3.5 w-3.5" /> Text saved
                </p>
              )}

              {saveError && (
                <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">{saveError}</div>
              )}

              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">{error}</div>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleGenerateAgain}
                  disabled={isLoading || isSaving}
                  className="flex-1 px-3 py-2.5 text-sm font-medium bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-md hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-sm"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Generating…
                    </>
                  ) : (
                    'Generate again'
                  )}
                </button>
                {(categoryId || folderId) && (
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving || saved || isLoading}
                    className="flex-1 px-3 py-2.5 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                  >
                    {isSaving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : saved ? (
                      <><Check className="h-4 w-4 text-green-600" /> Saved</>
                    ) : (
                      <><Bookmark className="h-4 w-4" /> Save</>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSaving}
                  className="flex-1 px-3 py-2.5 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
